<!--
  - Copyright (c) 2021 brightlamp
  -->

<template>
    <div>
        <v-alert
            v-model="copyTestLinkAlert"
            dismissible
            type="success">
            Test link copied to clipboard.
        </v-alert>
        <v-toolbar flat color="white">
            <v-toolbar-title>Test Requests</v-toolbar-title>
            <v-spacer/>
            <v-text-field
                v-model="searchComputed"
                append-icon="mdi-magnify"
                label="Search by Patient ID, Group ID, or Name"
                single-line
                hide-details
                clearable
                @click:clear="handleClearSearch"
                v-debounce:300="handleSearch">
            </v-text-field>
            <v-spacer/>
            <PatientDataDialog :dialog="patientDataDialog" :newPatient="newPatient" @dialog-close="patientDataDialogClose"/>
            <PatientSearchDialog :dialog="patientSearchDialog" :newTest="newTest" @dialog-close="patientSearchDialogClose"/>
            <TestRequestDialog :dialog="testRequestDialog" @dialog-close="testRequestDialogClose"/>
        </v-toolbar>
        <v-toolbar flat color="white">
            <v-btn class="mx-2" @click="newTestRequest" color="accent">
                <span style="color: #000000;">New Test</span>
            </v-btn>
            <v-btn class="ml-2" @click="newPatientData" color="accent">
                <span style="color: #000000;">New Patient</span>
            </v-btn>
            <v-icon large class="ml-6" @click="loadItems">
                mdi-refresh
            </v-icon>
            <v-spacer/>
            <v-checkbox
                class="mt-5"
                type="checkbox"
                true-value="true"
                label="See Organization Test Requests"
                v-model="seeOrganizationTestRequestsComputed"
                color="primary"
                v-if="currentUser && currentUser.orgId"
                @change="seeOrganizationTestRequestsToggle"/>
        </v-toolbar>
        <v-container fluid>
            <v-row no-gutters>
                <v-col>
                    <v-data-table
                        v-model="selected"
                        :headers="headers"
                        :items="testRequests"
                        item-key="id"
                        :must-sort=false
                        :multi-sort=true
                        @click:row="rowSelected"
                        @current-items="currentItems"
                        :single-expand=true
                        :expanded.sync="expanded"
                        :options.sync="paginationComputed"
                        :server-items-length="totalTestRequests"
                        :loading="waiting"
                        :footer-props="{'items-per-page-options':[15, 30, 50, 100]}"
                        class="elevation-1"
                        @update:options="loadItems">
                        <template v-slot:item.testViewed="{ item }">
                            <v-badge
                                :value = "item.testsPendingViewCount"
                                :color="item.testsPendingViewCount ? 'red' : 'white'"
                                :content=item.testsPendingViewCount>
                                {{ item.testViewed }}
                            </v-badge>
                        </template>
                        <template v-slot:item.actions="{ item }">
                            <v-menu offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn color="accent" v-on="on">
                                        <span style="color: #000000;">Actions</span>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item
                                        v-for="(actionItem, index) in actionItems" v-if="(index === 0 && item.allowEdit) || index === 1 || index === 2 || index === 3"
                                        :key="index"
                                        @click="testAction(index, item)">
                                            <v-list-item-icon><v-icon color="black" v-text="actionItem.icon"></v-icon></v-list-item-icon>
                                            <v-list-item-content><v-list-item-title v-text="actionItem.title"></v-list-item-title></v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                        <template v-slot:item.expanded="{ item }">
                            <v-row justify-Left>
                                <v-icon large v-if="!expanded.includes(item)">mdi-chevron-right</v-icon>
                                <v-icon large v-else>mdi-chevron-down</v-icon>
                            </v-row>
                        </template>
                        <template v-slot:expanded-item="props">
                            <td :colspan=12>
                                <v-expand-transition>
                                    <v-card class="elevation-10">
                                        <v-card-text>
                                            <ListMrnTests :mrnPredicate="mrnPredicate" :showExportCheckboxes=false @refreshMsg="refreshMsg"/>
                                        </v-card-text>
                                    </v-card>
                                </v-expand-transition>
                            </td>
                        </template>
                        <template v-slot:item.tokenExpireDate="{ item }">
                            {{item.tokenExpireDate !== 'N/A' ? new Date(item.tokenExpireDate).toLocaleString() : item.tokenExpireDate}}
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-container>
        <ConfirmationDialog v-if="confirmDeleteTestRequest" :params="confirmDeleteTestRequestParams" @confirmation-dialog-response="confirmDeleteTestRequestHandler"></ConfirmationDialog>
    </div>
</template>

<script>
    import { mapState, mapActions, mapMutations } from 'vuex'
    import ListMrnTests from '@/components/list-mrn-tests'
    import ConfirmationDialog from '@/components/confirmation-dialog'
    import PatientDataDialog from '@/components/patient-data-dialog';
    import PatientSearchDialog from '@/components/patient-search-dialog';
    import TestRequestDialog from '@/components/test-request-dialog'

    export default
    {
        name: 'telehealth-dashboard',
        components:
        {
            ListMrnTests,
            ConfirmationDialog,
            PatientDataDialog,
            PatientSearchDialog,
            TestRequestDialog
        },
        data: function ()
        {
            return {
                copyTestLinkAlert: false,
                mrnPredicate: {},
                mrn: {},
                expandOrHideRow: true,
                pagination: {},
                selected: [],
                expanded: [],
                headers:
                [
                    {
                        text: 'Tests',
                        value: 'expanded',
                        width: '40px',
                        sortable: false
                    },
                    {
                        text: 'Actions',
                        value: 'actions',
                        width: '100px',
                        sortable: false
                    },
                    {
                        text: 'Patient ID',
                        value: 'mrn',
                        width: '240px'
                    },
                    {
                        text: 'Name',
                        value: 'name',
                        width: '240px'
                    },
                    {
                        text: 'Group ID',
                        value: 'groupId',
                        width: '240px'
                    },
                    {
                        text: 'Request Date',
                        value: 'requestDate',
                        width: '200px'
                    },
                    {
                        text: 'Request Expire Date',
                        value: 'tokenExpireDate',
                        width: '200px'
                    },
                    {
                        text: 'Eye Tested',
                        value: 'eyeTested',
                        width: '110px',
                        sortable: false
                    },
                    {
                        text: 'Baseline',
                        value: 'baselineDisplay',
                        sortable: false
                    },
                    {
                        text: 'Test Tag',
                        value: 'testTag',
                        sortable: false
                    },
                    {
                        text: 'Requester',
                        value: 'displayName',
                        width: '240px',
                        sortable: false
                    }
                ],
                actionItems:
                [
                    { title: 'Edit Request', icon: 'mdi-pencil' },
                    { title: 'Delete Request', icon: 'mdi-delete' },
                    { title: 'Edit Patient', icon: 'mdi-pencil' },
                    { title: 'Copy Test Link', icon: 'mdi-content-copy' }
                ],
                newTest: false,
                confirmDeleteTestRequest: false,
                confirmDeleteTestRequestParams:
                {
                    title: 'Delete Test Request',
                    text: '',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No',
                    context: ''
                },
                patientDataDialog: false,
                patientSearchDialog: false,
                testRequestDialog: false,
                newPatient: false
            }
        },
        destroyed()
        {
            this.CLEAR_TESTS();
            this.CLEAR_TEST_REQUESTS()
        },
        computed:
        {
            ...mapState('auth',
            {
                currentUser: state => state.currentUser,
                seeOrganizationTests: state => state.seeOrganizationTests,
                searchValue: state => state.searchValue
            }),
            ...mapState('telehealth',
            {
                testRequests: state => state.testRequests,
                testRequestToMutate: state => state.testRequestToMutate,
                totalTestRequests: state => state.totalTestRequests
            }),
            ...mapState('tests',
            {
                patientData: state => state.patientData,
                tests: state => state.tests
            }),
            ...mapState('common',
            {
                waiting: state => state.waiting
            }),
            seeOrganizationTestRequestsComputed:
            {
                get: function ()
                {
                    return this.seeOrganizationTests
                },
                set: function (newValue)
                {
                    this.SET_SEE_ORGANIZATION_TESTS(newValue);
                    this.currentUser.preferences.seeOrganizationTests = newValue;
                    this.UPDATE_CURRENT_USER_NO_WAIT(this.currentUser)
                }
            },
            searchComputed:
            {
                get: function ()
                {
                    return this.searchValue
                },
                set: function (newValue)
                {
                    this.pagination.page = 1;
                    this.SET_SEARCH_VALUE(newValue)
                }
            },
            paginationComputed:
            {
                get: function ()
                {
                    if (!this.pagination.sortBy || 0 === this.pagination.sortBy.length)
                    {
                        if (!this.pagination.sortBy)
                        {
                            this.pagination =
                            {
                                sortBy: [],
                                sortDesc: []
                            }
                        }
                        // Use the user's saved preferences if they have them
                        if (this.currentUser?.preferences?.telehealthRequestListSort?.length > 0)
                        {
                            for (const sortItem of this.currentUser.preferences.telehealthRequestListSort)
                            {
                                this.pagination.sortBy.push(sortItem.sortValue);
                                this.pagination.sortDesc.push(-1 === sortItem.sortOrder);
                            }
                        }
                        else // Else just use a default
                        {
                            this.pagination.sortBy.push('testViewed');
                            this.pagination.sortBy.push('requestDate');
                            this.pagination.sortDesc.push(false);
                            this.pagination.sortDesc.push(true)
                        }
                    }
                    return this.pagination
                },
                set: function (newValue)
                {
                    this.pagination = newValue;
                    // If the user has no sorting preferences yet
                    if (!this.currentUser.preferences.telehealthRequestListSort || 0 === this.currentUser.preferences.telehealthRequestListSort.length)
                    {
                        this.convertSortPreferencesForSaving(newValue)
                    }
                    else // Else the user has preferences
                    {
                        let saveSortCriteria = false;
                        // If the number of sort criteria items is the same as what the user already had, continue to check for a difference
                        if (this.currentUser.preferences.telehealthRequestListSort.length === newValue.sortBy.length)
                        {
                            let index = 0;
                            for (const sortBy of newValue.sortBy)
                            {
                                // If a sort by item has changed, stop looking and proceed with saving the preference
                                if (sortBy !== this.currentUser.preferences.telehealthRequestListSort[index].sortValue)
                                {
                                    saveSortCriteria = true;
                                    break
                                }
                                // If a sort order item has changed, stop looking and proceed with saving the preference
                                if ((newValue.sortDesc[index] ? -1 : 1) !== this.currentUser.preferences.telehealthRequestListSort[index].sortOrder)
                                {
                                    saveSortCriteria = true;
                                    break
                                }
                            }
                        }
                        else // Else the number of sort criteria items are different, so proceed with saving the preferences
                        {
                            saveSortCriteria = true
                        }
                        // Just exit if there is nothing new to save
                        if (!saveSortCriteria)
                        {
                            return
                        }
                        this.convertSortPreferencesForSaving(newValue)
                    }
                    this.UPDATE_CURRENT_USER_NO_WAIT(this.currentUser).catch()
                }
            }
        },
        methods:
        {
            ...mapActions('telehealth', ['GET_TEST_REQUESTS', 'DELETE_TEST_REQUEST']),
            ...mapMutations('telehealth', ['CLEAR_TEST_REQUESTS', 'INIT_TEST_REQUEST', 'SET_TEST_REQUESTS', 'UPDATE_MUTATED_TEST_REQUEST']),
            ...mapMutations('tests', ['CLEAR_TESTS', 'INIT_PATIENT_DATA', 'UPDATE_MUTATED_PATIENT_DATA']),
            ...mapActions('tests', ['GET_TESTS']),
            ...mapMutations('auth', ['SET_SEE_ORGANIZATION_TESTS', 'SET_SEARCH_VALUE']),
            ...mapActions('auth', ['UPDATE_CURRENT_USER', 'UPDATE_CURRENT_USER_NO_WAIT']),
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            async handleSearch()
            {
                await this.loadItems()
            },
            async handleClearSearch()
            {
                this.searchComputed = '';
                await this.loadItems()
            },
            rowSelected(item)
            {
                // Do not expand or hide the row if an action in a row was selected
                if (!this.expandOrHideRow)
                {
                    this.expandOrHideRow = true;
                    return
                }
                else
                {
                    this.expanded = this.expanded.length > 0 && this.expanded[0].id === item.id ? [] : [item]
                }
                this.mrnPredicate = {mrn: item.mrn};
                this.mrn = item
            },
            currentItems(items)
            {
                if (this.paginationComputed.page !== 1 && 0 === items.length)
                {
                    this.paginationComputed.page -= 1
                }
            },
            async loadItems()
            {
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_TEST_REQUESTS({pagination: this.paginationComputed, searchValue: this.searchComputed, userOnly: !this.seeOrganizationTests})
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.WAITING(false)
            },
            async testAction(index, item)
            {
                switch (index)
                {
                    case 0: // Edit test request
                        await this.editTestRequest(item);
                        break
                    case 1: // Delete test request
                        this.deleteTestRequest(item);
                        break
                    case 2:
                        await this.editPatientData(item)
                        break
                    default: // Edit patient data
                        await navigator.clipboard.writeText(`${process.env.VUE_APP_PORTAL_URL}/anonymous/telehealth-redirect?testToken=${item.testToken}`);
                        this.copyTestLinkAlert = true;
                        setTimeout(() =>
                        {
                            this.copyTestLinkAlert = false;
                        }, 2000)
                        break
                }
            },
            newTestRequest()
            {
                this.INIT_TEST_REQUEST({telehealthEmailSubject: this.currentUser.telehealthEmailSubject});
                this.newTest = true;
                this.patientSearchDialog = true
            },
            async editTestRequest(item)
            {
                this.shouldCollapse(item.testToken);
                this.UPDATE_MUTATED_TEST_REQUEST(item);
                if (await this.loadPatientData(item.mrn))
                {
                    this.mrn = item.mrn;
                    this.newTest = false
                    // You should call it on the next frame
                    window.requestAnimationFrame(() =>
                    {
                        this.testRequestDialog = true
                    })
                }
            },
            async patientSearchDialogClose(payload)
            {
                this.patientSearchDialog = false;
                if (payload)
                {
                    if (await this.loadPatientData(payload.mrn))
                    {
                        this.mrn = payload.mrn;
                        // You should call it on the next frame
                        window.requestAnimationFrame(() =>
                        {
                            this.testRequestDialog = true
                        })
                    }
                }
            },
            async loadPatientData(mrn)
            {
                this.HIDE_SNACKBAR();
                this.WAITING(true);
                try
                {
                    await this.GET_TESTS({mrn: mrn});
                    this.UPDATE_MUTATED_PATIENT_DATA(this.patientData);
                    this.testRequestToMutate.mrn = mrn;
                    this.testRequestToMutate.name = this.patientData.name;
                    return true
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                    return false
                }
                finally
                {
                    this.WAITING(false)
                }
            },
            async testRequestDialogClose(payload)
            {
                this.testRequestDialog = false
                if (payload)
                {
                    // If we were passed back a patient ID use it in the search
                    if (payload.mrn)
                    {
                        this.SET_SEARCH_VALUE(payload.mrn)
                    }
                    if (payload.refresh)
                    {
                        await this.refreshMsg()
                    }
                }
            },
            newPatientData()
            {
                this.INIT_PATIENT_DATA();
                this.newPatient = true;
                this.patientDataDialog = true
            },
            async editPatientData(item)
            {
                this.shouldCollapse(item.testToken);
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_TESTS({mrn: item.mrn});
                    this.UPDATE_MUTATED_PATIENT_DATA(this.patientData);
                    this.newPatient = false;
                    this.patientDataDialog = true
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message)
                }
                finally
                {
                    this.WAITING(false)
                }
            },
            deleteTestRequest(item)
            {
                this.expandOrHideRow = false;
                this.confirmDeleteTestRequestParams.text = `Are you sure you want to delete Test Request: ${item.mrn}?`;
                this.confirmDeleteTestRequestParams.context = item;
                this.confirmDeleteTestRequest = true
            },
            async confirmDeleteTestRequestHandler(payload)
            {
                this.confirmDeleteTestRequest = false;
                // If the Test Request should be deleted
                if (payload.response)
                {
                    try
                    {
                        this.HIDE_SNACKBAR();
                        this.WAITING(true);
                        this.mrnPredicate = {};
                        this.mrn = {};
                        // Collapse all the rows
                        this.expanded = [];
                        await this.DELETE_TEST_REQUEST({testToken: payload.context.testToken});
                        await this.loadItems()
                    }
                    catch (error)
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message);
                    }
                    this.WAITING(false)
                }
            },
            async refreshMsg()
            {
                await this.loadItems()
            },
            async seeOrganizationTestRequestsToggle()
            {
                await this.loadItems()
            },
            shouldCollapse(testToken)
            {
                this.expandOrHideRow = false;
                // Collapse the row if switching to a new row
                if (this.expanded.length === 1 && this.expanded[0].testToken !== testToken)
                {
                    this.expanded = []
                }
            },
            convertSortPreferencesForSaving(newValue)
            {
                this.currentUser.preferences.telehealthRequestListSort = [];
                // Try and use the ones selected
                let index = 0;
                for (const sortBy of newValue.sortBy)
                {
                    const sortItem =
                    {
                        sortValue: sortBy,
                        sortOrder: newValue.sortDesc[index] ? -1 : 1
                    };
                    this.currentUser.preferences.telehealthRequestListSort.push(sortItem);
                    index++
                }
                // If no preferences were selected, use defaults
                if (0 === newValue.sortBy.length)
                {
                    this.currentUser.preferences.telehealthRequestListSort = [{sortValue: 'testViewed', sortItem: 1}, {sortValue: 'requestDate', sortItem: -1}]
                }
            },
            patientDataDialogClose()
            {
                this.patientDataDialog = false
            }
        }
    };
</script>

<style scoped>
</style>